import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mic, MicOff, Phone, AlertCircle, Calendar, ArrowLeft, Volume2, Book, CloudSun } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n/translations';

export default function SeniorInterface() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [reminders, setReminders] = useState([]);
  const recognitionRef = useRef(null);

  useEffect(() => {
    fetchUser();
    fetchReminders();
    
    // CRITICAL: Pre-load voices immediately on component mount
    if (window.speechSynthesis) {
      // Trigger voice loading
      window.speechSynthesis.getVoices();
      
      // Listen for voices loaded event
      window.speechSynthesis.onvoiceschanged = () => {
        const voices = window.speechSynthesis.getVoices();
        console.log(`✓✓✓ ${voices.length} VOICES LOADED ON MOUNT ✓✓✓`);
        voices.slice(0, 5).forEach(v => console.log(`  - ${v.name} (${v.lang})`));
      };
    }
  }, [userId]);

  useEffect(() => {
    if (user) {
      initializeSpeechRecognition();
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}`);
      setUser(res.data);
    } catch (error) {
      toast.error('User not found');
      navigate('/dashboard');
    }
  };

  const fetchReminders = async () => {
    try {
      const res = await axios.get(`${API}/reminders/${userId}/today`);
      setReminders(res.data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.maxAlternatives = 1;
      
      // CRITICAL: Set to user's profile language initially
      // Backend will auto-detect and respond in correct language
      const langMap = {
        'en': 'en-US',
        'de': 'de-DE',
        'ar': 'ar-SA',
        'tr': 'tr-TR',
        'ur': 'ur-PK'
      };
      
      // Use user profile language as default for recognition
      const targetLang = langMap[user?.language] || 'de-DE';  // Default to German
      recognitionRef.current.lang = targetLang;
      
      console.log('✓ Speech recognition initialized with language:', targetLang);

      recognitionRef.current.onresult = async (event) => {
        const text = event.results[0][0].transcript;
        setTranscript(text);
        await processVoiceCommand(text);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        toast.error('Voice recognition error. Please try again.');
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    } else {
      toast.error('Voice recognition not supported in this browser');
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
      setTranscript('');
      setResponse('');
    }
  };

  const processVoiceCommand = async (text) => {
    try {
      const res = await axios.post(`${API}/voice/process`, {
        user_id: userId,
        text: text
      });

      const responseText = res.data.response;
      
      console.log('=== VOICE RESPONSE ===');
      console.log('Response Text:', responseText);
      console.log('User Language:', user?.language);
      
      setResponse(responseText);
      
      // WICHTIG: Verwende IMMER die Benutzer-Profil-Sprache
      // Kein automatisches Sprachwechseln!
      setTimeout(() => {
        console.log('🔊 Speaking with user profile language:', user?.language);
        speak(responseText);
      }, 100);

      // Refresh reminders if needed
      if (res.data.intent === 'create_reminder' || res.data.intent === 'list_reminders') {
        await fetchReminders();
      }
    } catch (error) {
      console.error('Error processing voice command:', error);
      const errorMsg = 'I had trouble processing that. Please try again.';
      setResponse(errorMsg);
      speak(errorMsg);
    }
  };

  const speak = (text) => {
    if (!text) {
      console.error('❌ No text to speak');
      return;
    }

    if (!window.speechSynthesis) {
      console.error('❌ Speech synthesis not supported');
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    // Check if voices are available
    const availableVoices = window.speechSynthesis.getVoices();
    if (availableVoices.length === 0) {
      console.warn('⚠️ No voices available yet - waiting...');
      // Wait a bit and try again
      setTimeout(() => {
        const retryVoices = window.speechSynthesis.getVoices();
        if (retryVoices.length > 0) {
          console.log('✓ Voices loaded on retry');
          speak(text); // Retry
        } else {
          console.error('❌ Still no voices available after retry');
        }
      }, 200);
      return;
    }
    
    // Map language codes to voice locales
    const langMap = {
      'en': 'en-US',
      'de': 'de-DE',
      'ar': 'ar-SA',
      'tr': 'tr-TR',
      'ur': 'ur-PK'
    };
    
    const targetLang = langMap[user?.language] || 'de-DE';
    console.log('🔊 Speaking in:', targetLang);
    console.log('📝 Text:', text.substring(0, 80));
    
    // Create utterance
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = targetLang;
    utterance.rate = 0.85;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    // Event handlers
    utterance.onerror = (event) => {
      console.error('❌ Speech error:', event.error, event.message);
    };
    
    utterance.onstart = () => {
      console.log('✅ Speech STARTED');
    };
    
    utterance.onend = () => {
      console.log('✅ Speech COMPLETED');
    };
    
    // Function to select voice and speak
    const selectVoiceAndSpeak = () => {
      const voices = window.speechSynthesis.getVoices();
      console.log(`Available voices: ${voices.length}`);
      
      if (voices.length === 0) {
        console.warn('No voices available yet');
        return false;
      }
      
      // Language-specific voice selection with prioritization
      let matchingVoice = null;
      const langCode = user?.language || 'en';
      
      // Turkish voice selection - prioritize quality voices
      if (langCode === 'tr') {
        // First try: Google Turkish (best quality and pronunciation)
        matchingVoice = voices.find(voice => 
          (voice.lang === 'tr-TR' || voice.lang.startsWith('tr')) && 
          voice.name.toLowerCase().includes('google')
        );
        
        // Second try: Microsoft Turkish native voices (Emel, Seda)
        if (!matchingVoice) {
          matchingVoice = voices.find(voice => 
            (voice.lang === 'tr-TR' || voice.lang.startsWith('tr')) && 
            (voice.name.includes('Emel') || voice.name.includes('Seda') || voice.name.includes('Tolga'))
          );
        }
        
        // Third try: Any Turkish voice with exact locale match
        if (!matchingVoice) {
          matchingVoice = voices.find(voice => voice.lang === 'tr-TR');
        }
        
        // Fourth try: Any Turkish voice
        if (!matchingVoice) {
          matchingVoice = voices.find(voice => voice.lang.startsWith('tr'));
        }
        
        // Log selected voice for debugging
        if (matchingVoice) {
          console.log('✓ Turkish voice selected:', matchingVoice.name, matchingVoice.lang);
        } else {
          console.warn('⚠️ No Turkish voice found! Available voices:', voices.filter(v => v.lang.startsWith('tr')).map(v => `${v.name} (${v.lang})`));
        }
      }
      // German voice selection
      else if (langCode === 'de') {
        // First try: Google German
        matchingVoice = voices.find(voice => 
          (voice.lang === 'de-DE' || voice.lang.startsWith('de')) && 
          voice.name.toLowerCase().includes('google')
        );
        
        // Second try: Microsoft German voices (Hedda, Katja)
        if (!matchingVoice) {
          matchingVoice = voices.find(voice => 
            (voice.lang === 'de-DE' || voice.lang.startsWith('de')) && 
            (voice.name.includes('Hedda') || voice.name.includes('Katja') || voice.name.includes('Anna'))
          );
        }
        
        // Third try: Any German voice
        if (!matchingVoice) {
          matchingVoice = voices.find(voice => voice.lang.startsWith('de'));
        }
        
        if (matchingVoice) {
          console.log('✓ German voice selected:', matchingVoice.name, matchingVoice.lang);
        }
      }
      // Arabic voice selection
      else if (langCode === 'ar') {
        matchingVoice = voices.find(voice => 
          (voice.lang === 'ar-SA' || voice.lang.startsWith('ar')) && 
          voice.name.toLowerCase().includes('google')
        );
        if (!matchingVoice) {
          matchingVoice = voices.find(voice => voice.lang.startsWith('ar'));
        }
        if (matchingVoice) {
          console.log('✓ Arabic voice selected:', matchingVoice.name, matchingVoice.lang);
        }
      }
      // Default: exact match or starts with language code
      else {
        matchingVoice = voices.find(voice => voice.lang === targetLang);
        if (!matchingVoice) {
          matchingVoice = voices.find(voice => voice.lang.toLowerCase().startsWith(langCode));
        }
      }
      
      if (matchingVoice) {
        utterance.voice = matchingVoice;
        console.log('✓ Voice selected:', matchingVoice.name);
      } else {
        console.warn('⚠️ No voice for', targetLang, '- using system default');
      }
      
      // Speak immediately
      console.log('🎤 Speaking now...');
      window.speechSynthesis.speak(utterance);
    };
    
    // Get voices and speak
    const voices = window.speechSynthesis.getVoices();
    
    if (voices.length > 0) {
      console.log(`✓ ${voices.length} voices ready`);
      selectVoiceAndSpeak();
    } else {
      console.log('⏳ Waiting for voices...');
      
      // Try multiple times
      let attempts = 0;
      const maxAttempts = 10;
      
      const trySpeak = setInterval(() => {
        const v = window.speechSynthesis.getVoices();
        attempts++;
        
        if (v.length > 0) {
          console.log(`✓ Voices ready after ${attempts * 100}ms`);
          clearInterval(trySpeak);
          selectVoiceAndSpeak();
        } else if (attempts >= maxAttempts) {
          console.warn('⚠️ Timeout - speaking with system default');
          clearInterval(trySpeak);
          window.speechSynthesis.speak(utterance);
        }
      }, 100);
    }
  };

  const triggerEmergency = async () => {
    try {
      const res = await axios.post(`${API}/emergency/trigger`, {
        user_id: userId,
        trigger_type: 'button'
      });
      
      let message = '';
      if (user?.language === 'de') {
        message = `Notfall ausgelöst. ${res.data.contacts?.length || 0} Kontakte werden benachrichtigt.`;
      } else if (user?.language === 'tr') {
        message = `Acil durum tetiklendi. ${res.data.contacts?.length || 0} kişi bilgilendirilecek.`;
      } else if (user?.language === 'ar') {
        message = `تم تفعيل الطوارئ. سيتم إخطار ${res.data.contacts?.length || 0} جهة اتصال.`;
      } else {
        message = `Emergency alert sent to ${res.data.contacts?.length || 0} contacts.`;
      }
      
      toast.error(user?.language === 'de' ? 'Notfall ausgelöst!' : user?.language === 'tr' ? 'Acil Durum!' : 'Emergency Alert Sent!', {
        description: message
      });
      
      console.log('🔊 Speaking emergency message:', message);
      speak(message);
      setResponse(message);
    } catch (error) {
      const errorMsg = user?.language === 'de' 
        ? 'Notfall konnte nicht ausgelöst werden'
        : user?.language === 'tr'
        ? 'Acil durum gönderilemedi'
        : 'Failed to send emergency alert';
      toast.error(errorMsg);
      speak(errorMsg);
    }
  };

  const readReminders = () => {
    let text = '';
    
    if (reminders.length === 0) {
      if (user?.language === 'de') {
        text = 'Sie haben heute keine Erinnerungen';
      } else if (user?.language === 'tr') {
        text = 'Bugün hiç hatırlatıcınız yok';
      } else if (user?.language === 'ar') {
        text = 'ليس لديك تذكيرات اليوم';
      } else {
        text = 'You have no reminders for today';
      }
    } else {
      const titles = reminders.slice(0, 3).map(r => r.title).join(', ');
      
      if (user?.language === 'de') {
        text = `Sie haben ${reminders.length} Erinnerung${reminders.length > 1 ? 'en' : ''} heute: ${titles}`;
      } else if (user?.language === 'tr') {
        text = `Bugün ${reminders.length} hatırlatıcınız var: ${titles}`;
      } else if (user?.language === 'ar') {
        text = `لديك ${reminders.length} تذكير اليوم: ${titles}`;
      } else {
        text = `You have ${reminders.length} reminder${reminders.length > 1 ? 's' : ''} today: ${titles}`;
      }
    }
    
    console.log('🔊 Speaking reminders:', text);
    speak(text);
    setResponse(text);
  };

  const speakPrayerInfo = async () => {
    try {
      // Fetch prayer times from backend
      const res = await axios.get(`${API}/prayer/times/${userId}`);
      const times = res.data;
      
      // Get current time to find next prayer
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      // Prayer times in order
      const prayers = [
        { name: 'Fajr', nameDE: 'Fadschr', nameTR: 'Sabah', time: times.Fajr },
        { name: 'Dhuhr', nameDE: 'Dhuhr', nameTR: 'Öğle', time: times.Dhuhr },
        { name: 'Asr', nameDE: 'Asr', nameTR: 'İkindi', time: times.Asr },
        { name: 'Maghrib', nameDE: 'Maghrib', nameTR: 'Akşam', time: times.Maghrib },
        { name: 'Isha', nameDE: 'Ischā', nameTR: 'Yatsı', time: times.Isha }
      ];
      
      // Find next prayer
      let nextPrayer = null;
      for (const prayer of prayers) {
        const [hours, minutes] = prayer.time.split(':').map(Number);
        const prayerTime = hours * 60 + minutes;
        if (prayerTime > currentTime) {
          nextPrayer = prayer;
          break;
        }
      }
      
      // If no prayer found today, next is Fajr tomorrow
      if (!nextPrayer) {
        nextPrayer = prayers[0];
      }
      
      // Create response based on language
      let text = '';
      if (user?.language === 'de') {
        text = `Das nächste Gebet ist ${nextPrayer.nameDE} um ${nextPrayer.time} Uhr. Heutige Gebetszeiten: Fadschr ${times.Fajr}, Dhuhr ${times.Dhuhr}, Asr ${times.Asr}, Maghrib ${times.Maghrib}, Ischā ${times.Isha}`;
      } else if (user?.language === 'tr') {
        text = `Bir sonraki namaz ${nextPrayer.nameTR} saat ${nextPrayer.time}'de. Bugünün namaz vakitleri: Sabah ${times.Fajr}, Öğle ${times.Dhuhr}, İkindi ${times.Asr}, Akşam ${times.Maghrib}, Yatsı ${times.Isha}`;
      } else if (user?.language === 'ar') {
        text = `الصلاة التالية ${nextPrayer.name} في الساعة ${nextPrayer.time}`;
      } else {
        text = `Next prayer is ${nextPrayer.name} at ${nextPrayer.time}. Today's prayer times: Fajr ${times.Fajr}, Dhuhr ${times.Dhuhr}, Asr ${times.Asr}, Maghrib ${times.Maghrib}, Isha ${times.Isha}`;
      }
      
      speak(text);
      setResponse(text);
    } catch (error) {
      console.error('Error fetching prayer times:', error);
      const errorText = user?.language === 'de' 
        ? 'Gebetszeiten konnten nicht abgerufen werden. Bitte überprüfen Sie Ihren Standort in den Einstellungen.'
        : user?.language === 'tr'
        ? 'Namaz vakitleri alınamadı. Lütfen ayarlardaki konumunuzu kontrol edin.'
        : 'Could not fetch prayer times. Please check your location in settings.';
      speak(errorText);
      setResponse(errorText);
      toast.error(errorText);
    }
  };

  const speakWeather = async () => {
    try {
      setResponse(''); // Clear previous response
      const res = await axios.post(`${API}/voice/process`, {
        user_id: userId,
        text: user?.language === 'de' ? 'Wie ist das Wetter?' : user?.language === 'tr' ? 'Hava durumu nasıl?' : 'What is the weather?'
      });
      
      const weatherText = res.data.response;
      console.log('🔊 Speaking weather:', weatherText);
      speak(weatherText);
      setResponse(weatherText);
    } catch (error) {
      console.error('Error fetching weather:', error);
      const errorText = user?.language === 'de' 
        ? 'Wetterdaten konnten nicht abgerufen werden. Bitte überprüfen Sie Ihren Standort in den Einstellungen.'
        : user?.language === 'tr'
        ? 'Hava durumu bilgisi alınamadı. Lütfen ayarlardaki konumunuzu kontrol edin.'
        : 'Could not fetch weather data. Please check your location in settings.';
      speak(errorText);
      setResponse(errorText);
      toast.error(errorText);
    }
  };

  const testVoice = () => {
    const testText = user?.language === 'de' 
      ? 'Dies ist ein Test der deutschen Sprachausgabe. Wie hört sich das an?'
      : 'This is a test of the speech output. How does this sound?';
    speak(testText);
    setResponse(testText);
    
    // Log available voices
    const voices = window.speechSynthesis.getVoices();
    console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-100 p-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <Button
          data-testid="back-to-dashboard-btn"
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('backToDashboard', user?.language)}
        </Button>
        
        <Card className="glass-effect">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 mb-4">
              <img 
                src="/logo.svg" 
                alt="Senior Voice Assistant Logo" 
                className="w-16 h-16 rounded-full shadow-lg"
              />
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                  {t('hello', user?.language)}, {user.name}!
                </h1>
              </div>
            </div>
            <p className="text-lg text-gray-600">{t('howCanIHelp', user?.language)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Voice Interface */}
      <div className="max-w-4xl mx-auto mb-6">
        <Card data-testid="voice-interface-card" className="glass-effect">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <button
                data-testid="voice-toggle-btn"
                onClick={toggleListening}
                className={`mx-auto w-32 h-32 rounded-full flex items-center justify-center transition-all ${
                  isListening 
                    ? 'bg-red-500 hover:bg-red-600 voice-active' 
                    : 'bg-indigo-600 hover:bg-indigo-700'
                } shadow-xl`}
              >
                {isListening ? (
                  <MicOff className="w-16 h-16 text-white" />
                ) : (
                  <Mic className="w-16 h-16 text-white" />
                )}
              </button>
              
              <p className="mt-6 text-xl font-semibold text-gray-900">
                {isListening ? t('listening', user?.language) : t('tapToSpeak', user?.language)}
              </p>
              
              {transcript && (
                <div data-testid="transcript-display" className="mt-6 p-4 bg-neutral-900/80 backdrop-blur-xl rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 mb-1">{t('youSaid', user?.language)}</p>
                  <p className="text-lg text-gray-900">{transcript}</p>
                </div>
              )}
              
              {response && (
                <div data-testid="response-display" className="mt-4 p-4 bg-indigo-50 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Volume2 className="w-5 h-5 text-indigo-600" />
                    <p className="text-sm text-indigo-600 font-medium">{t('response', user?.language)}</p>
                  </div>
                  <p className="text-lg text-gray-900">{response}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Button
            data-testid="emergency-btn"
            onClick={triggerEmergency}
            className="senior-btn bg-red-600 hover:bg-red-700 text-white"
          >
            <AlertCircle className="w-8 h-8 mr-3" />
            <span>{t('emergencyHelp', user?.language)}</span>
          </Button>

          <Button
            data-testid="reminders-btn"
            onClick={readReminders}
            className="senior-btn bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Calendar className="w-8 h-8 mr-3" />
            <div className="text-left">
              <span className="block">{t('todaysReminders', user?.language)}</span>
              {reminders.length > 0 && (
                <Badge className="mt-1" variant="secondary">
                  {reminders.length} {t('pending', user?.language)}
                </Badge>
              )}
            </div>
          </Button>

          {user.modules?.prayer_times && (
            <Button
              data-testid="prayer-times-btn"
              onClick={speakPrayerInfo}
              className="senior-btn bg-green-600 hover:bg-green-700 text-white"
            >
              <Book className="w-8 h-8 mr-3" />
              <span>{t('prayerTimes', user?.language)}</span>
            </Button>
          )}

          {user.modules?.weather && (
            <Button
              data-testid="weather-btn"
              onClick={speakWeather}
              className="senior-btn bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              <CloudSun className="w-8 h-8 mr-3" />
              <span>
                {user?.language === 'de' ? 'Wetter' : user?.language === 'tr' ? 'Hava Durumu' : 'Weather'}
              </span>
            </Button>
          )}

          <Button
            data-testid="contacts-btn"
            onClick={() => {
              const msg = user?.language === 'de' 
                ? 'Um jemanden anzurufen, sagen Sie Ruf an, gefolgt vom Namen'
                : 'To call someone, say Call followed by their name';
              speak(msg);
            }}
            className="senior-btn bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Phone className="w-8 h-8 mr-3" />
            <span>{t('callContacts', user?.language)}</span>
          </Button>
        </div>
      </div>

      {/* Today's Reminders Display */}
      {reminders.length > 0 && (
        <div className="max-w-4xl mx-auto mt-6">
          <Card data-testid="reminders-list-card">
            <CardContent className="pt-6">
              <h3 className="text-xl font-bold mb-4">{t('todaysSchedule', user?.language)}</h3>
              <div className="space-y-3">
                {reminders.map((reminder) => (
                  <div
                    key={reminder.id}
                    data-testid={`reminder-item-${reminder.id}`}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-lg text-gray-900">{reminder.title}</p>
                        {reminder.description && (
                          <p className="text-gray-600 mt-1">{reminder.description}</p>
                        )}
                      </div>
                      <p className="text-sm font-medium text-indigo-600">
                        {new Date(reminder.scheduled_time).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}